import { Link } from "react-router-dom";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import Logo from "../components/Logo";
import {
  Truck, ArrowRight, MapTrifold, Headset, ReceiptX, Wrench, UserPlus,
  CheckCircle, Sparkle, Phone
} from "@phosphor-icons/react";

const SERVICES = [
  {
    icon: MapTrifold,
    title: "AI Dispatch Automation",
    tagline: "Reduce manual dispatch work and improve communication with drivers.",
    items: [
      "Load assignment automation",
      "Driver notifications",
      "Delivery updates",
      "Customer shipment tracking",
      "Automated dispatch workflows",
    ],
  },
  {
    icon: Headset,
    title: "AI Customer Service Assistant",
    tagline: "Provide fast responses to customers 24 hours a day.",
    items: [
      "Shipment status updates",
      "Frequently asked questions",
      "Quote requests",
      "Customer support automation",
      "Lead capture",
    ],
  },
  {
    icon: ReceiptX,
    title: "Invoice & Billing Automation",
    tagline: "Get paid faster and reduce paperwork.",
    items: [
      "Automatic invoice creation",
      "Payment reminders",
      "Billing workflows",
      "Customer payment tracking",
    ],
  },
  {
    icon: Wrench,
    title: "Fleet Maintenance Automation",
    tagline: "Prevent costly breakdowns and improve equipment reliability.",
    items: [
      "Service reminders",
      "Maintenance scheduling",
      "Vehicle maintenance records",
      "Repair tracking",
    ],
  },
  {
    icon: UserPlus,
    title: "Driver Recruiting Automation",
    tagline: "Simplify hiring and onboarding.",
    items: [
      "Driver application management",
      "Automated follow-up",
      "Interview scheduling",
      "Onboarding workflows",
    ],
  },
];

const PLANS = [
  {
    name: "Starter",
    fleet: "1 to 10 trucks",
    setup: "$2,500",
    monthly: "$500 / month",
    features: [
      "AI Dispatch Automation",
      "Driver & customer messaging",
      "Invoice & payment reminders",
      "Email support",
    ],
    accent: false,
    testid: "plan-starter",
  },
  {
    name: "Growth",
    fleet: "10 to 50 trucks",
    setup: "$5,000",
    monthly: "$750 / month",
    features: [
      "Everything in Starter",
      "Fleet maintenance automation",
      "Driver recruiting workflows",
      "Priority support",
      "Onboarding & workflow review",
    ],
    accent: true,
    testid: "plan-growth",
  },
  {
    name: "Enterprise",
    fleet: "50+ trucks",
    setup: "Custom",
    monthly: "Custom pricing",
    features: [
      "Everything in Growth",
      "Custom integrations (DAT, Truckstop, factoring)",
      "Dedicated success manager",
      "SLA & white-glove onboarding",
      "Advanced analytics",
    ],
    accent: false,
    testid: "plan-enterprise",
  },
];

export default function ServicesPage() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash === "#pricing") {
      const el = document.getElementById("pricing");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      window.scrollTo({ top: 0 });
    }
  }, [location]);

  return (
    <div className="min-h-screen relative noise-bg overflow-hidden" data-testid="services-page">
      {/* Header */}
      <header className="relative z-10 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 sm:py-5 flex items-center justify-between gap-3">
          <Link to="/" className="min-w-0" data-testid="services-logo-link">
            <Logo />
          </Link>
          <div className="hidden md:flex items-center gap-6 text-sm">
            <Link to="/" data-testid="services-home-link" className="text-neutral-400 hover:text-white transition-colors">Home</Link>
            <Link to="/contact" data-testid="services-contact-link" className="text-neutral-400 hover:text-white transition-colors">Contact</Link>
            <Link to="/login" data-testid="services-signin-link" className="text-neutral-400 hover:text-white transition-colors">Sign in</Link>
            <Link to="/signup" data-testid="services-getstarted-btn" className="btn-primary rounded-full px-4 py-2 inline-flex items-center gap-2">
              Get started <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
          <Link to="/signup" className="md:hidden btn-primary rounded-full px-3 py-1.5 text-xs whitespace-nowrap">Try free</Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10 max-w-7xl mx-auto px-8 pt-24 pb-16">
        <div className="max-w-3xl">
          <div className="overline mb-4">OUR SERVICES</div>
          <h1 className="font-display text-5xl md:text-6xl font-semibold tracking-tight leading-[1.05]">
            Automation built for the way <span className="text-[#FF5A1F]">trucking really works.</span>
          </h1>
          <p className="text-neutral-400 text-lg mt-6 leading-relaxed">
            FreightPilot AI plugs into every part of your operation — dispatch, drivers, customers, billing,
            maintenance, and recruiting — so your team focuses on freight, not forms.
          </p>
        </div>
      </section>

      {/* Services */}
      <section className="relative z-10 max-w-7xl mx-auto px-8 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {SERVICES.map((s, i) => (
            <div
              key={i}
              data-testid={`service-card-${i}`}
              className="card-surface p-8 hover-lift"
            >
              <div className="flex items-start gap-4 mb-6">
                <div className="w-11 h-11 rounded-md bg-[#FF5A1F]/10 border border-[#FF5A1F]/25 flex items-center justify-center shrink-0">
                  <s.icon size={22} weight="regular" color="#FF5A1F" />
                </div>
                <div>
                  <h3 className="font-display text-2xl font-semibold tracking-tight">{s.title}</h3>
                  <p className="text-neutral-400 mt-1 leading-relaxed">{s.tagline}</p>
                </div>
              </div>
              <div className="overline mb-3">SERVICES INCLUDE</div>
              <ul className="space-y-2">
                {s.items.map((it, k) => (
                  <li key={k} className="flex items-start gap-3 text-sm text-neutral-200">
                    <CheckCircle size={16} weight="fill" color="#FF5A1F" className="mt-0.5 shrink-0" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="relative z-10 max-w-7xl mx-auto px-8 py-24 border-t border-white/10 scroll-mt-24">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <div className="overline mb-4">PRICING</div>
          <h2 className="font-display text-5xl font-semibold tracking-tight leading-tight">
            Simple pricing, real ROI.
          </h2>
          <p className="text-neutral-400 mt-6 text-lg leading-relaxed">
            Every plan starts with a <span className="text-white">free workflow review</span> —
            we identify automation opportunities and cost savings before you pay a dollar.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {PLANS.map((p) => (
            <div
              key={p.name}
              data-testid={p.testid}
              className={`p-8 rounded-lg border transition-colors flex flex-col ${
                p.accent
                  ? "border-[#FF5A1F]/50 bg-[#FF5A1F]/[0.03]"
                  : "border-white/10 bg-[#141414]"
              }`}
            >
              {p.accent && (
                <div className="pill pill-primary self-start mb-4">
                  <Sparkle size={12} weight="fill" /> MOST POPULAR
                </div>
              )}
              <div className="font-display text-3xl font-semibold tracking-tight">{p.name}</div>
              <div className="text-sm text-neutral-500 mt-1 mb-8">Perfect for fleets with {p.fleet}.</div>

              <div className="border-t border-white/5 pt-6 mb-6 space-y-3">
                <div className="flex items-baseline justify-between">
                  <span className="overline">SETUP</span>
                  <span className="font-mono text-xl font-semibold">{p.setup}</span>
                </div>
                <div className="flex items-baseline justify-between">
                  <span className="overline">MONTHLY</span>
                  <span className={`font-mono text-xl font-semibold ${p.accent ? "text-[#FF5A1F]" : ""}`}>{p.monthly}</span>
                </div>
              </div>

              <ul className="space-y-2 mb-8 flex-1">
                {p.features.map((f, k) => (
                  <li key={k} className="flex items-start gap-2 text-sm text-neutral-300">
                    <CheckCircle size={14} weight="fill" color={p.accent ? "#FF5A1F" : "#10B981"} className="mt-1 shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <Link
                to="/signup"
                data-testid={`${p.testid}-cta`}
                className={`w-full rounded-full py-3 text-sm text-center font-medium transition-colors ${
                  p.accent
                    ? "btn-primary"
                    : "border border-white/15 text-neutral-200 hover:border-white/30 hover:text-white"
                }`}
              >
                {p.setup === "Custom" ? "Talk to sales" : "Start with " + p.name}
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* Free consultation */}
      <section className="relative z-10 max-w-7xl mx-auto px-8 py-24 border-t border-white/10">
        <div className="card-surface p-12 md:p-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div className="max-w-xl">
            <div className="overline mb-3 text-[#FF5A1F]">FREE CONSULTATION</div>
            <h3 className="font-display text-3xl md:text-4xl font-semibold tracking-tight leading-tight">
              Every new client gets a free workflow review.
            </h3>
            <p className="text-neutral-400 mt-4 leading-relaxed">
              We look at how your fleet operates today — dispatching, paperwork, driver comms —
              and identify exactly where automation saves you time and money. No commitment.
            </p>
          </div>
          <div className="flex flex-col gap-3 shrink-0">
            <Link
              to="/signup"
              data-testid="consult-signup-btn"
              className="btn-primary rounded-full px-8 py-4 inline-flex items-center justify-center gap-2"
            >
              Book my free review <ArrowRight size={16} weight="bold" />
            </Link>
            <a
              href="tel:+16056797303"
              data-testid="consult-call-btn"
              className="rounded-full px-8 py-4 inline-flex items-center justify-center gap-2 border border-white/15 hover:border-white/30 text-neutral-200 hover:text-white transition-colors text-sm"
            >
              <Phone size={14} weight="bold" /> Call (605) 679-7303
            </a>
          </div>
        </div>
      </section>

      <footer className="relative z-10 border-t border-white/10 py-8 text-center text-xs text-neutral-500">
        FREIGHTPILOT AI • Trucking, made smart and easy
      </footer>
    </div>
  );
}
